import { images } from './business'

export type Service = {
  id: string
  index: string
  title: string
  summary: string
  body: string[]
  bullets: string[]
  image?: string
  imageAlt?: string
}

export const services: Service[] = [
  {
    id: 'gordijnrailsystemen',
    index: '01',
    title: 'Gordijnrailsystemen',
    summary:
      'Handbediende gordijnrailsystemen met een strak profiel, een vloeiende loop en een montagewijze die past bij de architectuur van de ruimte.',
    body: [
      'Een gordijnrail is het onderdeel van een interieur dat het vaakst wordt aangeraakt en het minst mag opvallen. Onze gordijnrailsystemen zijn daarom opgebouwd rond twee uitgangspunten: een loop die licht en stil blijft, ook na jaren dagelijks gebruik, en een profiel dat zich visueel terugtrekt zodat de stof het beeld bepaalt.',
      'De systemen zijn leverbaar voor wandmontage, plafondmontage en inbouw in een koof of plafondsparing. Juist die laatste toepassing wordt in hedendaagse interieurs veel gevraagd: inbouw gordijnrails verdwijnen volledig in het plafond, waardoor het gordijn ogenschijnlijk uit de constructie valt. Aluminium gordijnrails vormen hierbij de basis, met glijders en runners die zijn afgestemd op het gewicht van de stof.',
      'Voor grotere raampartijen zijn meerbaans opstellingen mogelijk, bijvoorbeeld een inbetween met een verduisterend gordijn in één rail. Bochten, hoeken en rondingen worden op maat gebogen, zodat ook erkers, ronde wanden en vrijstaande bedopstellingen met één doorlopende rail kunnen worden uitgevoerd.',
    ],
    bullets: [
      'Wand-, plafond- en inbouwmontage',
      'Eén-, twee- en meerbaans opstellingen',
      'Aluminium profielen, op maat afgekort',
      'Bochten en rondingen op tekening',
    ],
    image: images.railProfile,
    imageAlt: 'Wit aluminium gordijnrailprofiel met glijders, zijaanzicht',
  },
  {
    id: 'elektrische-gordijnrails',
    index: '02',
    title: 'Elektrische gordijnrails',
    summary:
      'Een elektrische gordijnrail beweegt de stof rustig en gelijkmatig, en laat zich aansturen vanaf een schakelaar, app of gebouwsysteem.',
    body: [
      'Bij grote of hooggeplaatste raampartijen is handbediening niet altijd praktisch. Een elektrische gordijnrail neemt die beweging over en zorgt dat de stof elke keer op dezelfde manier opent en sluit. Dat verlengt de levensduur van het gordijn en voorkomt beschadiging van de rail, omdat er niet aan de stof wordt getrokken.',
      'De aandrijving is geïntegreerd in het systeem en wordt akoestisch zorgvuldig afgestemd: het geluidsniveau is een ontwerpcriterium, niet een bijproduct. Een gordijnrail elektrisch uitvoeren betekent ook nadenken over voeding, kabelroutes en de plek van de motor in het plafond — onderdelen die wij in de engineeringfase samen met de installateur vastleggen.',
      'Aansturing is mogelijk via wandschakelaars, handzenders, tijdschema\'s of koppeling met gebouwautomatisering. Daardoor kunnen elektrische gordijnrails meebewegen met zonwering, verlichtingsscènes of het dag- en nachtritme van een gebouw.',
    ],
    bullets: [
      'Geïntegreerde, stille aandrijving',
      'Bediening via schakelaar, zender of gebouwsysteem',
      'Voorbereiding van voeding en kabelroutes',
      'Geschikt voor hoge en brede raampartijen',
    ],
    image: images.railMotor,
    imageAlt: 'Gemotoriseerde gordijnrail met gebogen profiel en compacte motorunit',
  },
  {
    id: 'gemotoriseerde-gordijnrails',
    index: '03',
    title: 'Gemotoriseerde gordijnrails',
    summary:
      'Gemotoriseerde gordijnrails voor situaties waarin comfort, herhaalbaarheid en gebruiksgemak zwaarder wegen dan handbediening.',
    body: [
      'Waar een elektrische gordijnrail vooral de beweging automatiseert, gaat het bij gemotoriseerde gordijnrails om het complete scenario: welke gordijnen bewegen samen, hoe snel, tot welke positie en op welk moment. In hotelkamers, vergaderruimtes, auditoria en high-end woningen is dat het verschil tussen een technische voorziening en een ervaring.',
      'Wij stemmen de motorkeuze af op het gewicht van de stof, de lengte van de baan en de gewenste snelheid. Bij zeer lange banen of zware verduisteringsstoffen wordt de aandrijving daarop gedimensioneerd, zodat de rail niet aan de bovengrens van zijn capaciteit werkt. Dat is bepalend voor de betrouwbaarheid op lange termijn.',
      'Ook onderhoud en beheer horen bij het ontwerp. Motoren en besturing zijn bereikbaar zonder dat een plafond gesloopt hoeft te worden, en posities kunnen opnieuw worden ingeregeld wanneer een ruimte een andere functie krijgt.',
    ],
    bullets: [
      'Motorkeuze op basis van stofgewicht en baanlengte',
      'Instelbare eindposities en scenario\'s',
      'Bereikbaar voor service en herinregeling',
      'Toepasbaar in hospitality, kantoor en residentieel',
    ],
  },
  {
    id: 'projectmatige-gordijnrails',
    index: '04',
    title: 'Projectmatige gordijnrails',
    summary:
      'Gordijnrails voor projecten: van uitwerking op tekening en maatvoering tot gefaseerde levering per bouwdeel.',
    body: [
      'Projectmatige gordijnrails vragen een andere aanpak dan een losse rail. Er zijn tekeningen, revisies, bouwfaseringen en meerdere partijen die op elkaar wachten. Wij werken daarom vanaf de ontwerpfase mee: we lezen plafondtekeningen, bepalen de sparingen, leggen de maatvoering vast en leveren gordijnrails op maat per ruimte, verdieping of bouwdeel.',
      'Voor gordijnrails voor projecten met veel repetitie — hotelkamers, patiëntenkamers, studentenwoningen — stellen we een vast configuratieprincipe op dat per type wordt vastgelegd. Dat beperkt fouten op de bouw en maakt nalevering jaren later nog eenvoudig, omdat de specificatie herleidbaar blijft.',
      'Bij bijzondere geometrie, zoals ronde wanden, schuine plafonds of vrije vormen in een atrium, werken we het systeem uit op tekening voordat er geproduceerd wordt. Zo weet de aannemer wat er komt en weet de architect dat het beeld overeind blijft.',
    ],
    bullets: [
      'Uitwerking op plafond- en detailtekening',
      'Vaste configuraties per kamertype',
      'Gefaseerde levering per bouwdeel',
      'Oplossingen voor afwijkende geometrie',
    ],
    image: images.railMulti,
    imageAlt: 'Meerbaans aluminium gordijnrailsysteem voor projectmatige toepassing',
  },
  {
    id: 'zorggordijnrails',
    index: '05',
    title: 'Zorggordijnrails',
    summary:
      'Gordijnrails voor de zorg: rails voor privacygordijnen rond bedden, behandelplekken en onderzoeksruimtes.',
    body: [
      'In een zorgomgeving wordt een gordijn tientallen keren per dag bewogen, vaak met één hand en meestal met haast. Gordijnrails zorg vraagt daarom om een systeem dat licht loopt, geen piek in de beweging heeft en tegen intensief gebruik kan. Net zo belangrijk: het gordijn moet snel af kunnen voor reiniging en daarna weer zonder gereedschap terug.',
      'Voor gordijnrails ziekenhuis en andere zorginstellingen werken we met plafondgemonteerde en ingebouwde uitvoeringen rond bedden, behandelstoelen en onderzoeksplekken. Hoeken en rondingen worden op maat gebogen zodat een bedplek volledig kan worden afgeschermd zonder dat de rail onderbroken wordt. Waar medische apparatuur, tilliften of infuusrails aanwezig zijn, stemmen we de positie van het railsysteem daarop af.',
      'Omdat zorgvastgoed vaak in gebruik blijft tijdens een verbouwing, plannen we levering en montagevoorbereiding per afdeling of kamerreeks. Zo blijft de overlast voor bewoners, patiënten en personeel beperkt.',
    ],
    bullets: [
      'Plafond- en inbouwrails rond bedden en behandelplekken',
      'Gordijnen eenvoudig afneembaar voor reiniging',
      'Gebogen rails voor volledige afscherming',
      'Afstemming op medische apparatuur en tilsystemen',
    ],
    image: images.interiorSheer,
    imageAlt: 'Lichte ruimte met vitrage over de volledige wand, rustige daglichtinval',
  },
  {
    id: 'commerciele-gordijnrails',
    index: '06',
    title: 'Commerciële gordijnrails',
    summary:
      'Commerciële gordijnrails voor hotels, kantoren, publieke gebouwen en horeca, waar beeld en belasting samen moeten kloppen.',
    body: [
      'In commercieel vastgoed heeft een gordijn meerdere taken: zon weren, geluid dempen, ruimtes verdelen en het interieur afmaken. Commerciële gordijnrails moeten die taken jarenlang aankunnen in een omgeving waarin schoonmaakploegen, gasten en wisselende gebruikers het systeem dagelijks aanraken.',
      'Wij leveren rails voor hotelkamers en suites, vergader- en conferentieruimtes, restaurants, lobby\'s, kantoortuinen en multifunctionele zalen. Bij grote zaalindelingen worden lange banen toegepast waarmee een ruimte in delen kan worden opgesplitst; bij hotelkamers gaat het juist om een strak, herhaalbaar detail dat in elke kamer identiek is.',
      'Waar het interieurbeeld strak moet blijven, kiezen veel opdrachtgevers voor plafond gordijnrails of volledige inbouw. De combinatie met een elektrische of gemotoriseerde aandrijving maakt het mogelijk om gordijnen centraal te bedienen, bijvoorbeeld bij het klaarzetten van een zaal of het sluiten van een gebouw.',
    ],
    bullets: [
      'Hotels, kantoren, horeca en publieke gebouwen',
      'Lange banen voor ruimteverdeling',
      'Herhaalbaar detail voor kamerreeksen',
      'Combineerbaar met centrale bediening',
    ],
    image: images.interiorSeaView,
    imageAlt: 'Hospitality-interieur met witte vitrage en rode overgordijnen langs een raampartij',
  },
]

export type Sector = {
  title: string
  text: string
}

export const sectors: Sector[] = [
  {
    title: 'Hospitality',
    text: 'Hotelkamers, suites, lobby\'s en zalen waarin elk detail zich herhaalt en de bediening intuïtief moet zijn voor een gast die er één nacht verblijft.',
  },
  {
    title: 'Healthcare',
    text: 'Ziekenhuizen, klinieken en zorginstellingen waar privacy, hygiëne en intensief dagelijks gebruik de eisen aan het railsysteem bepalen.',
  },
  {
    title: 'Architectuur',
    text: 'Projecten waarin de rail onderdeel is van het plafonddetail en de sparing al in de constructietekening wordt meegenomen.',
  },
  {
    title: 'High-end residential',
    text: 'Woningen waarin grote raampartijen, zware stoffen en gemotoriseerde bediening samenkomen in een onopvallend geheel.',
  },
]
